import { useForm } from "@tanstack/react-form";

/**
 * The review's single form: one entry per ordinary contact id, plus `hausarzt`.
 *
 * Wrapping `useForm` here exists for typing rather than behaviour. `ReactFormExtendedApi` takes
 * twelve generic parameters and `AnyFormApi` does not expose `.Field`/`.Subscribe`, so child
 * components take `ReturnType<typeof useReviewForm>` and let inference supply all of them instead of
 * hand-writing a signature that would drift from the real one.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const form = useReviewForm({ "itm-0": "a@b.de", hausarzt: "" }, async () => {});
 * ```
 */
export function useReviewForm(
  defaultValues: Record<string, string>,
  onSubmit: (input: { value: Record<string, string> }) => Promise<void> | void,
) {
  return useForm({ defaultValues, onSubmit });
}

/**
 * The concrete form type the review's child components accept.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * function Editor({ form }: { form: ReviewForm }) {
 *   return <form.Field name="hausarzt">{() => null}</form.Field>;
 * }
 * ```
 */
export type ReviewForm = ReturnType<typeof useReviewForm>;
